/** 契约解析：把 `unknown` 的响应体变成有类型的对象，形状不对就抛 `ContractError`。
 *
 * 这里**不做任何默认值填充**。缺字段填 0、填空数组，都会把「接口没按契约回答」
 * 伪装成「账户没有钱 / 没有订单」——那是伪造业务成功，规格上明令禁止。
 *
 * 覆盖面：§3.1–3.4（status / orders / fills / positions / runs）与 §3.9（snapshots）。
 */

import {
  type QueryValue,
  query,
  type ApiDeps,
  expectList,
  expectRecord,
  optionalNumber,
  optionalString,
  requireBoolean,
  requireNumber,
  requireString,
} from "./client";
import type {
  BalancePayload,
  BarRow,
  DataSnapshot,
  OrderRecord,
  PositionRecord,
  RecoveryRecord,
  RejectedManifest,
  Rerun,
  RunDetail,
  RunRecord,
  SnapshotFileEntry,
  SnapshotRange,
  SnapshotRecord,
  SnapshotsPayload,
  StatusPayload,
  TradeRecord,
  HaltState, PaperEvent, SignalsPayload, SignalRecord, BridgeReport, RefItem, DailySnapshotsPayload, DailySnapshot, AlertRecord, DailyReportsPayload, DailyReport, EquityPayload, JobStatus,
} from "./types";
import { apiUrl } from "./client";
import { ContractError } from "./errors";

/** P2 端点返回的原始契约对象；页面只展示服务端实际给出的字段。 */
export type P2Payload = Record<string, unknown> | Record<string, unknown>[];
export function parseP2(body: unknown, url: string): P2Payload {
  if (Array.isArray(body)) return body.map((item) => expectRecord(item, url, "P2 列表项"));
  return expectRecord(body, url, "P2 响应");
}
export function readP2(path: string, params?: Record<string, QueryValue>): Promise<P2Payload> {
  const url = apiUrl(path);
  return query<unknown>(path, params).then((body) => parseP2(body, url));
}

const URL_STATUS = apiUrl("/status");
const URL_ORDERS = apiUrl("/orders");
const URL_FILLS = apiUrl("/fills");
const URL_POSITIONS = apiUrl("/positions");
const URL_RUNS = apiUrl("/runs");
const URL_SNAPSHOTS = apiUrl("/snapshots");
const URL_DATA_SNAPSHOT = apiUrl("/data-snapshot");

export function parseDataSnapshot(body: unknown): DataSnapshot {
  const source = expectRecord(body, URL_DATA_SNAPSHOT, "数据快照响应");
  const bars = expectList(source["bars"], "bars", URL_DATA_SNAPSHOT, "行情").map((item) => {
    const row = expectRecord(item, URL_DATA_SNAPSHOT, "行情记录");
    return {
      instrument_id: requireString(row, "instrument_id", URL_DATA_SNAPSHOT),
      open: requireNumber(row, "open", URL_DATA_SNAPSHOT),
      high: requireNumber(row, "high", URL_DATA_SNAPSHOT),
      low: requireNumber(row, "low", URL_DATA_SNAPSHOT),
      close: requireNumber(row, "close", URL_DATA_SNAPSHOT),
      volume: requireNumber(row, "volume", URL_DATA_SNAPSHOT),
      ts_event: requireNumber(row, "ts_event", URL_DATA_SNAPSHOT),
    } satisfies BarRow;
  });
  const rawSignals = source["signals"];
  if (!Array.isArray(rawSignals)) throw new Error(`${URL_DATA_SNAPSHOT}: signals 必须是数组`);
  return {
    snapshot_id: optionalString(source, "snapshot_id"),
    bars_path: requireString(source, "bars_path", URL_DATA_SNAPSHOT),
    signals_path: requireString(source, "signals_path", URL_DATA_SNAPSHOT),
    bars,
    signals: rawSignals.map((item) => expectRecord(item, URL_DATA_SNAPSHOT, "信号记录")),
  };
}

export function readDataSnapshot(): Promise<DataSnapshot> {
  return query<unknown>("/data-snapshot").then(parseDataSnapshot);
}

/** §3.1 系统状态。 */
export function parseStatus(body: unknown): StatusPayload {
  const source = expectRecord(body, URL_STATUS, "status 响应");
  const balanceSource = expectRecord(source["balance"], URL_STATUS, "status.balance");
  const balance: BalancePayload = {
    balanced: requireBoolean(balanceSource, "balanced", URL_STATUS),
    checked: requireNumber(balanceSource, "checked", URL_STATUS),
    // 字段在才带：null = 后端明说「还没对过账」；缺 = 旧后端没给，不替它下结论。
    ...("checked_run" in balanceSource ? { checked_run: optionalString(balanceSource, "checked_run") } : {}),
  };
  const recoveries = Array.isArray(source["recoveries"]) ? source["recoveries"] : [];
  return {
    halted: requireBoolean(source, "halted", URL_STATUS),
    halt_reason: optionalString(source, "halt_reason"),
    halt_since_ns: optionalNumber(source, "halt_since_ns"),
    ledger_run: optionalString(source, "ledger_run"),
    ledger_mode: optionalString(source, "ledger_mode"),
    recoveries: recoveries.map((item, index) => parseRecovery(item, index)),
    config_digest: requireString(source, "config_digest", URL_STATUS),
    currency: requireString(source, "currency", URL_STATUS),
    starting_cash: requireNumber(source, "starting_cash", URL_STATUS),
    cash: requireNumber(source, "cash", URL_STATUS),
    realized_pnl: requireNumber(source, "realized_pnl", URL_STATUS),
    balance,
    running: optionalString(source, "running"),
    current_run: optionalString(source, "current_run"),
  };
}

function parseRecovery(item: unknown, index: number): RecoveryRecord {
  const source = expectRecord(item, URL_STATUS, `status.recoveries[${index}]`);
  return {
    operator: requireString(source, "operator", URL_STATUS),
    reason: requireString(source, "reason", URL_STATUS),
    ts_event: requireNumber(source, "ts_event", URL_STATUS),
    halt_reason: optionalString(source, "halt_reason"),
  };
}

/** §3.2 订单（`Ledger.orders_report()`）。 */
export function parseOrders(body: unknown): OrderRecord[] {
  return expectList(body, "orders", URL_ORDERS, "订单").map((source) => ({
    client_order_id: requireString(source, "client_order_id", URL_ORDERS),
    venue_order_id: optionalString(source, "venue_order_id"),
    instrument_id: optionalString(source, "instrument_id") ?? "",
    side: optionalString(source, "side"),
    quantity: optionalNumber(source, "quantity"),
    status: requireString(source, "status", URL_ORDERS),
    ts_event: requireNumber(source, "ts_event", URL_ORDERS),
    reason: optionalString(source, "reason"),
    filled_qty: optionalNumber(source, "filled_qty") ?? 0,
  }));
}

/** §3.2 成交（`Ledger.trades_report()`）。 */
export function parseTrades(body: unknown): TradeRecord[] {
  return expectList(body, "trades", URL_FILLS, "成交").map((source) => {
    const trade: TradeRecord = {
      trade_id: requireString(source, "trade_id", URL_FILLS),
      client_order_id: requireString(source, "client_order_id", URL_FILLS),
      instrument_id: optionalString(source, "instrument_id") ?? "",
      side: requireString(source, "side", URL_FILLS),
      last_qty: requireNumber(source, "last_qty", URL_FILLS),
      last_px: requireNumber(source, "last_px", URL_FILLS),
      slipped_px: requireNumber(source, "slipped_px", URL_FILLS),
      commission: requireNumber(source, "commission", URL_FILLS),
      ts_event: requireNumber(source, "ts_event", URL_FILLS),
      position_id: optionalString(source, "position_id"),
    };
    // 账本里 `slippage_cost` 是 @property，接口「要显式带上」（§3.2）；真的没带时
    // 按同一条式子现算，并在页面上标注为推导值——不许静默当 0。
    const provided = optionalNumber(source, "slippage_cost");
    trade.slippage_cost = provided ?? derivedSlippage(trade);
    return trade;
  });
}

/** `TradeRecord.slippage_cost` 的同式推导（正数是成本）。 */
export function derivedSlippage(trade: Pick<TradeRecord, "side" | "last_qty" | "last_px" | "slipped_px">): number {
  const sign = trade.side === "BUY" ? 1 : -1;
  return sign * (trade.slipped_px - trade.last_px) * trade.last_qty;
}

/** §3.2 持仓（`Ledger.positions_report()`）。 */
export function parsePositions(body: unknown): PositionRecord[] {
  return expectList(body, "positions", URL_POSITIONS, "持仓").map((source) => ({
    position_id: requireString(source, "position_id", URL_POSITIONS),
    instrument_id: optionalString(source, "instrument_id") ?? "",
    side: requireString(source, "side", URL_POSITIONS),
    quantity: requireNumber(source, "quantity", URL_POSITIONS),
    avg_px_open: requireNumber(source, "avg_px_open", URL_POSITIONS),
    avg_px_close: optionalNumber(source, "avg_px_close"),
    realized_pnl: requireNumber(source, "realized_pnl", URL_POSITIONS),
    ts_event: requireNumber(source, "ts_event", URL_POSITIONS),
    closed: source["closed"] === true,
  }));
}

/** §3.9 数据快照：只读 manifest 目录的清单。
 *
 * 四个键一个不少地读：`snapshots` / `rejected` / `snapshot_dir` / `manifest_version`。
 * `rejected` 尤其不能省——「目录是空的」与「目录里有读不懂的东西」在界面上是两件事，
 * 少了它就分不清（`operations-console/spec.md` 业务规则 4）。
 */
export function parseSnapshots(body: unknown): SnapshotsPayload {
  const source = expectRecord(body, URL_SNAPSHOTS, "快照清单响应");
  return {
    snapshots: expectList(source, "snapshots", URL_SNAPSHOTS, "快照清单").map((item, index) =>
      parseSnapshot(item, URL_SNAPSHOTS, index),
    ),
    rejected: expectList(source, "rejected", URL_SNAPSHOTS, "快照坏文件台账").map((item) =>
      parseRejectedManifest(item, URL_SNAPSHOTS),
    ),
    snapshot_dir: requireString(source, "snapshot_dir", URL_SNAPSHOTS),
    manifest_version: requireNumber(source, "manifest_version", URL_SNAPSHOTS),
  };
}

function parseSnapshot(source: Record<string, unknown>, url: string, index: number): SnapshotRecord {
  const where = `快照清单.snapshots[${index}]`;
  const snapshot: SnapshotRecord = {
    snapshot_id: requireString(source, "snapshot_id", url),
    source: requireString(source, "source", url),
    row_count: requireNumber(source, "row_count", url),
    min_ts_ns: optionalNumber(source, "min_ts_ns"),
    max_ts_ns: optionalNumber(source, "max_ts_ns"),
    content_sha256: requireString(source, "content_sha256", url),
    query_range: parseSnapshotRange(source["query_range"], url, where),
  };
  // 附加字段只在 manifest 真的带了的时候透出（§3.9「没有的不补」）——
  // 补一个 null 会让「manifest 没写」与「manifest 写了空」长得一样。
  const asOf = optionalNumber(source, "as_of_ns");
  if (asOf !== null) snapshot.as_of_ns = asOf;
  if (source["files"] !== undefined) {
    snapshot.files = expectList(source, "files", url, `${where}.files`).map((item, fileIndex) => {
      const entry = expectRecord(item, url, `${where}.files[${fileIndex}]`);
      const file: SnapshotFileEntry = {
        path: requireString(entry, "path", url),
        sha256: requireString(entry, "sha256", url),
      };
      const rows = optionalNumber(entry, "row_count");
      if (rows !== null) file.row_count = rows;
      return file;
    });
  }
  return snapshot;
}

function parseSnapshotRange(value: unknown, url: string, where: string): SnapshotRange {
  const range = expectRecord(value, url, `${where}.query_range`);
  return {
    start_ns: optionalNumber(range, "start_ns"),
    end_ns: optionalNumber(range, "end_ns"),
  };
}

function parseRejectedManifest(source: Record<string, unknown>, url: string): RejectedManifest {
  return {
    file: requireString(source, "file", url),
    reason: requireString(source, "reason", url),
    detail: requireString(source, "detail", url),
  };
}

/** §3.4 运行索引。 */
export function parseRuns(body: unknown): RunRecord[] {
  return expectList(body, "runs", URL_RUNS, "运行索引").map((source) => parseRun(source, URL_RUNS));
}

export function parseRunDetail(body: unknown, runId: string): RunDetail {
  const url = apiUrl(`/runs/${encodeURIComponent(runId)}`);
  const source = expectRecord(body, url, "运行详情");
  const base = parseRun(source, url);
  const detail: RunDetail = { ...base };
  const commissions = optionalNumber(source, "commissions");
  if (commissions !== null) detail.commissions = commissions;
  const slippage = optionalNumber(source, "slippage_cost");
  if (slippage !== null) detail.slippage_cost = slippage;
  if (typeof source["halted"] === "boolean") detail.halted = source["halted"];
  const cash = optionalNumber(source, "cash");
  if (cash !== null) detail.cash = cash;
  const startingCash = optionalNumber(source, "starting_cash");
  if (startingCash !== null) detail.starting_cash = startingCash;
  const recon = source["reconciliation"];
  if (recon && typeof recon === "object") {
    const entry = expectRecord(recon, url, "运行详情 reconciliation");
    detail.reconciliation = {
      balanced: requireBoolean(entry, "balanced", url),
      checked: requireNumber(entry, "checked", url),
      summary: requireString(entry, "summary", url),
    };
  }
  const signals = source["signals"];
  if (signals && typeof signals === "object") {
    const entry = expectRecord(signals, url, "运行详情 signals");
    detail.signals = {
      accepted: stringList(entry["accepted"]),
      duplicates: stringList(entry["duplicates"]),
      rejected: Array.isArray(entry["rejected"])
        ? entry["rejected"].map((item, index) => {
            const row = expectRecord(item, url, `signals.rejected[${index}]`);
            return {
              signal_id: requireString(row, "signal_id", url),
              reason: requireString(row, "reason", url),
            };
          })
        : [],
    };
  }
  if (source["reruns"] !== undefined) detail.reruns = parseReruns(source["reruns"], url);
  return detail;
}

/** `reruns[]`：后端每条都写全七个键，可为 null 的保留 null；类型不符即契约错误（#4752）。 */
function parseReruns(value: unknown, url: string): Rerun[] {
  if (!Array.isArray(value)) throw new ContractError("字段 `reruns` 应为数组", url, value);
  const nullable = <T,>(row: Record<string, unknown>, key: string, type: "string" | "boolean"): T | null => {
    const v = row[key];
    if (v === null) return null;
    if (typeof v !== type) throw new ContractError(`reruns 字段 \`${key}\` 应为 ${type} 或 null`, url, row);
    return v as T;
  };
  return value.map((item, index) => {
    const row = expectRecord(item, url, `reruns[${index}]`);
    return {
      ts_utc: requireString(row, "ts_utc", url),
      ledger_digest: nullable<string>(row, "ledger_digest", "string"),
      operator: nullable<string>(row, "operator", "string"),
      reason: nullable<string>(row, "reason", "string"),
      dry_run: nullable<boolean>(row, "dry_run", "boolean"),
      same_as_previous: nullable<boolean>(row, "same_as_previous", "boolean"),
      archive: nullable<string>(row, "archive", "string"),
    };
  });
}

function parseRun(source: Record<string, unknown>, url: string): RunRecord {
  return {
    run_id: requireString(source, "run_id", url),
    mode: requireString(source, "mode", url),
    ts_ns: requireNumber(source, "ts_ns", url),
    orders: requireNumber(source, "orders", url),
    fills: requireNumber(source, "fills", url),
    realized_pnl: requireNumber(source, "realized_pnl", url),
    config_digest: requireString(source, "config_digest", url),
    config_digest_canonical: optionalString(source, "config_digest_canonical") ?? undefined,
    snapshot_id: optionalString(source, "snapshot_id") ?? "",
    ledger_digest: requireString(source, "ledger_digest", url),
    balanced: requireBoolean(source, "balanced", url),
    duration_s: requireNumber(source, "duration_s", url),
    total_pnl: optionalNumber(source, "total_pnl") ?? undefined,
    cash_change: optionalNumber(source, "cash_change") ?? undefined,
    strategy_id: optionalString(source, "strategy_id"),
    trade_date: optionalString(source, "trade_date"),
  };
}

function stringList(value: unknown): string[] {
  return Array.isArray(value) ? value.filter((item): item is string => typeof item === "string") : [];
}

export interface ReadDeps extends ApiDeps {
  params?: Record<string, QueryValue>;
}

export const readStatus = (deps: ReadDeps = {}): Promise<StatusPayload> =>
  query<unknown>("/status", deps.params, deps).then(parseStatus);

export const readOrders = (deps: ReadDeps = {}): Promise<OrderRecord[]> =>
  query<unknown>("/orders", deps.params, deps).then(parseOrders);

export const readFills = (deps: ReadDeps = {}): Promise<TradeRecord[]> =>
  query<unknown>("/fills", deps.params, deps).then(parseTrades);

export const readPositions = (deps: ReadDeps = {}): Promise<PositionRecord[]> =>
  query<unknown>("/positions", deps.params, deps).then(parsePositions);

export const readRuns = (deps: ReadDeps = {}): Promise<RunRecord[]> =>
  query<unknown>("/runs", deps.params, deps).then(parseRuns);

export const readSnapshots = (deps: ReadDeps = {}): Promise<SnapshotsPayload> =>
  query<unknown>("/snapshots", deps.params, deps).then(parseSnapshots);

export const readRunDetail = (runId: string, deps: ApiDeps = {}): Promise<RunDetail> =>
  query<unknown>(`/runs/${encodeURIComponent(runId)}`, undefined, deps).then((body) =>
    parseRunDetail(body, runId),
  );

function pObj(body: unknown, path: string, what: string): Record<string, unknown> { return expectRecord(body, apiUrl(path), what); }
function pStr(r: Record<string, unknown>, k: string, p: string): string { return requireString(r, k, apiUrl(p)); }
function pNum(r: Record<string, unknown>, k: string, p: string): number { return requireNumber(r, k, apiUrl(p)); }
function pBool(r: Record<string, unknown>, k: string, p: string): boolean { return requireBoolean(r, k, apiUrl(p)); }
function pStrList(r: Record<string, unknown>, k: string, p: string): string[] { const v=r[k]; if(!Array.isArray(v)||v.some(x=>typeof x!=="string")) throw new ContractError(`字段 \`${k}\` 应为字符串数组`,apiUrl(p),r); return v as string[]; }

export function parseHaltState(body: unknown): HaltState {
  const p = "/paper/state";
  const r = pObj(body, p, "停机状态");
  const strategies = expectRecord(r["strategies"], apiUrl(p), "停机状态策略");
  return {
    strategies: Object.fromEntries(Object.entries(strategies).map(([id, value]) => {
      const item = expectRecord(value, apiUrl(p), `策略 ${id}`);
      return [id, { halted: pBool(item, "halted", p), halt_reason: optionalString(item, "halt_reason"), last_trade_date: optionalString(item, "last_trade_date") }];
    })),
    halted: pBool(r, "halted", p),
    updated_utc: pStr(r, "updated_utc", p),
  };
}
export function parsePaperEvents(body: unknown): PaperEvent[] {
  const p = "/paper/events";
  return expectList(body, "events", apiUrl(p), "停机事件").map(x => ({
    ts_ns: pNum(x, "ts_ns", p), kind: pStr(x, "kind", p), strategy_id: pStr(x, "strategy_id", p),
    reason: pStr(x, "reason", p), operator: pStr(x, "operator", p),
  }));
}
function parseSignal(x: Record<string, unknown>, p: string): SignalRecord {
  const source = expectRecord(x["source"], apiUrl(p), "信号来源");
  return { signal_id: pStr(x, "signal_id", p), instrument_id: pStr(x, "instrument_id", p), direction: pStr(x, "direction", p), target_weight: pNum(x, "target_weight", p), valid_from: pNum(x, "valid_from", p), valid_until: pNum(x, "valid_until", p), source: { research_flow: pStr(source, "research_flow", p), version: pStr(source, "version", p), params_digest: pStr(source, "params_digest", p), data_snapshot: pStr(source, "data_snapshot", p) }, contract_version: pNum(x, "contract_version", p) };
}
export function parseSignals(body: unknown): SignalsPayload {
  const p = "/signals", r = pObj(body, p, "信号");
  return { accepted: expectList(r, "accepted", apiUrl(p), "受理信号").map(x => parseSignal(x, p)), duplicates: pStrList(r, "duplicates", p), rejected: expectList(r, "rejected", apiUrl(p), "拒收信号").map(x => ({ signal_id: pStr(x, "signal_id", p), reason: pStr(x, "reason", p) })) };
}
export function parseRefItem(x: Record<string, unknown>, p: string): RefItem {
  // 三个键必须都在（值可为 null）：缺键说明后端没按统一形状回答，不猜。
  for (const k of ["ref", "ref_kind", "reason"]) if (!(k in x)) throw new ContractError(`收件条目缺字段 \`${k}\``, apiUrl(p), x);
  return { ref: optionalString(x, "ref"), ref_kind: optionalString(x, "ref_kind"), reason: optionalString(x, "reason") };
}
function nullableRecord(r: Record<string, unknown>, k: string, p: string): Record<string, unknown> | null {
  if (!(k in r)) throw new ContractError(`缺字段 \`${k}\``, apiUrl(p), r);
  return r[k] === null ? null : expectRecord(r[k], apiUrl(p), k);
}
/** 单份收件（后端统一形状）：`rejected` / `no_order` 一律 `{ref, ref_kind, reason}`，`delivery` 可为 null。 */
export function parseBridgeReport(body: unknown): BridgeReport {
  const p = "/signals/inbox", r = pObj(body, p, "收件");
  const inputCount = r["input_count"];
  if (inputCount !== null && typeof inputCount !== "number") throw new ContractError("字段 `input_count` 应为数字或 null", apiUrl(p), r);
  return {
    strategy_id: pStr(r, "strategy_id", p), source_line: optionalString(r, "source_line"), date: pStr(r, "date", p), shape: pStr(r, "shape", p),
    source: optionalString(r, "source"), trade_date: pStr(r, "trade_date", p), input_count: inputCount as number | null,
    signals: expectList(r, "signals", apiUrl(p), "信号").map(x => parseSignal(x, p)),
    rejected: expectList(r, "rejected", apiUrl(p), "拒收").map(x => parseRefItem(x, p)),
    no_order: expectList(r, "no_order", apiUrl(p), "不下单").map(x => parseRefItem(x, p)),
    inputs: nullableRecord(r, "inputs", p), delivery: nullableRecord(r, "delivery", p),
    operator: optionalString(r, "operator"), reason: optionalString(r, "reason"),
    upstream_latest_date: optionalString(r, "upstream_latest_date"), empty_reason: optionalString(r, "empty_reason"),
  };
}
function parseDaily(x: Record<string, unknown>, p: string): DailySnapshot {
  const snap = x["snapshot_id"];
  if (snap !== null && typeof snap !== "string") throw new ContractError("字段 `snapshot_id` 应为字符串或 null", apiUrl(p), x);
  return {
    trade_date: pStr(x, "trade_date", p), status: pStr(x, "status", p), snapshot_id: snap as string | null, reused: pBool(x, "reused", p),
    symbols: pStrList(x, "symbols", p), missing_today: pStrList(x, "missing_today", p), regressions: pStrList(x, "regressions", p),
    rows: optionalNumber(x, "rows"), note: optionalString(x, "note"), halt_marker: pBool(x, "halt_marker", p),
    pending_manual: expectList(x, "pending_manual", apiUrl(p), "人工待办").map(m => ({ symbol: pStr(m, "symbol", p), action_type: optionalString(m, "action_type"), action_date: optionalString(m, "action_date"), raw: m["raw"] })),
    alerts: expectList(x, "alerts", apiUrl(p), "告警").map(item => parseAlert(item, p)),
  };
}
export function parseDailyDetail(body: unknown): DailySnapshot { const p = "/data/daily/{date}"; return parseDaily(pObj(body, p, "单日增量"), p); }
export function parseDailySnapshots(body: unknown): DailySnapshotsPayload { const p = "/data/daily"; return { snapshots: expectList(body, "snapshots", apiUrl(p), "每日快照").map(x => parseDaily(x, p)) }; }
function parseAlert(x: Record<string, unknown>, p: string): AlertRecord { return { ts_utc: pStr(x, "ts_utc", p), kind: pStr(x, "kind", p), summary: pStr(x, "summary", p), detail: pStr(x, "detail", p) }; }
export function parseAlerts(body: unknown): AlertRecord[] { const p = "/alerts"; return expectList(body, "alerts", apiUrl(p), "告警").map(x => parseAlert(x, p)); }
export function parseDailyReports(body: unknown): DailyReportsPayload { const p = "/reports/daily"; return { reports: expectList(body, "reports", apiUrl(p), "每日报告").map(x => { const r = expectRecord(x, apiUrl(p), "报告索引"); return { trade_date: pStr(r, "trade_date", p), status: pStr(r, "status", p), headline: [], strategies: {}, delivery: { headline_count: pNum(r, "headline_count", p), delivered: pBool(r, "delivered", p) } }; }) }; }
export function parseDailyReport(body: unknown): DailyReport { const p = "/reports/daily/{date}"; const r = pObj(body, p, "每日报告详情"); const headline = expectList(r, "headline", apiUrl(p), "headline").map(item => { const h = expectRecord(item, apiUrl(p), "headline"); return { kind: pStr(h, "kind", p), strategy_id: pStr(h, "strategy_id", p), detail: pStr(h, "detail", p) }; }); return { trade_date: pStr(r, "trade_date", p), status: pStr(r, "status", p), headline, strategies: expectRecord(r["strategies"], apiUrl(p), "报告策略") as DailyReport["strategies"], delivery: typeof r["delivery"] === "object" && r["delivery"] !== null ? r["delivery"] as Record<string, unknown> : undefined, summary_text: optionalString(r, "summary_text") ?? undefined }; }
export function parseEquity(body:unknown, strategy=""):EquityPayload{const p="/paper/equity";const rows=Array.isArray(body)?body:expectList(pObj(body,p,"权益曲线"),"points",apiUrl(p),"权益点");return {strategy,points:rows.map(x=>{const date=optionalString(x,"trade_date") ?? pStr(x,"date",p);const total=optionalNumber(x,"total_pnl");const cashChange=optionalNumber(x,"cash_change") ?? optionalNumber(x,"realized_pnl_cash");return {date,cash:pNum(x,"cash",p),pnl:total ?? cashChange ?? 0,fees:optionalNumber(x,"commissions") ?? optionalNumber(x,"fees") ?? 0,slippage:optionalNumber(x,"slippage_cost") ?? optionalNumber(x,"slippage") ?? 0,total_pnl:total ?? undefined,realized_pnl_closed:optionalNumber(x,"realized_pnl_closed") ?? undefined,cash_change:cashChange ?? undefined};})}}
export function parseJobs(body:unknown):JobStatus{const p="/jobs",r=pObj(body,p,"任务");return {job_id:pStr(r,"job_id",p),status:pStr(r,"status",p),progress:pNum(r,"progress",p),detail:pStr(r,"detail",p)}}

export const readHaltState=()=>query<unknown>("/paper/state").then(parseHaltState);
export const readPaperEvents=()=>query<unknown>("/paper/events").then(parsePaperEvents);
export const readSignals=(status?:string)=>query<unknown>("/signals",status?{status}:undefined).then(parseSignals);
export const readBridgeReport=(date:string, strategy:string)=>query<unknown>("/signals/inbox",{date,strategy}).then(parseBridgeReport);
export const readDailyDetail=(date:string)=>query<unknown>(`/data/daily/${encodeURIComponent(date)}`).then(parseDailyDetail);
export const readDailySnapshots=()=>query<unknown>("/data/daily").then(parseDailySnapshots);
export const readAlerts=()=>query<unknown>("/alerts").then(parseAlerts);
export const readDailyReports=()=>query<unknown>("/reports/daily").then(parseDailyReports);
export const readDailyReport=(date:string)=>query<unknown>(`/reports/daily/${encodeURIComponent(date)}`).then(parseDailyReport);
export const readEquity=(strategy:string)=>query<unknown>("/paper/equity",{strategy}).then(body=>parseEquity(body,strategy));
